/**
 * TemplateCard Component
 * 
 * Displays a summary card for a trip template, showing key information
 * like destination, duration, budget range, and difficulty level.
 */

import { useMemo } from 'react';
import { View, Pressable, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText, Body, Caption } from '../ui/Text';
import { Card } from '../ui/Card';
import { AppImage } from '../ui/AppImage';
import type { TripTemplate } from '@/types/tripTemplates';
import { SaveTemplateButton } from '@/components/recommendations/SaveTemplateButton';
import { useLandmarkImage } from '@/hooks/useLandmarkImage';
import { formatTemplateTagLabel } from '@/utils/templateTags';
import { shareTemplate } from '@/services/templateShare';
import { useToast } from '@/components/ui/ToastProvider';

type TemplateCardProps = {
  template: TripTemplate;
  onPress: (template: TripTemplate) => void;
  variant?: 'default' | 'compact';
  showSaveAction?: boolean;
  showShareAction?: boolean;
  userId?: string;
};

export function TemplateCard({
  template,
  onPress,
  variant = 'default',
  showSaveAction = false,
  showShareAction = true,
  userId,
}: TemplateCardProps) {
  const theme = useTheme();
  const { showSuccess } = useToast();
  const isCompact = variant === 'compact';
  const travelStyleKeys = new Set((template.travelStyle ?? []).map((s) => String(s).toLowerCase()));
  const templateTopicTags = (template.tags ?? []).filter(
    (tag) => String(tag).trim().length > 0 && !travelStyleKeys.has(String(tag).trim().toLowerCase()),
  );
  const visibleTopicTags = templateTopicTags.slice(0, 4);
  const hiddenTopicCount = templateTopicTags.length - visibleTopicTags.length;
  const { imageUrl } = useLandmarkImage(template.destination, template.coverImage);
  const displayImage =
    imageUrl ||
    template.coverImage ||
    'https://placehold.co/800x600?text=Destination+image+pending';
  const versionedDisplayImage = useMemo(() => {
    if (!displayImage || !/^https?:\/\//i.test(displayImage) || displayImage.includes('placehold.co')) {
      return displayImage;
    }

    const updatedAtMs = template.updatedAt instanceof Date ? template.updatedAt.getTime() : null;
    if (!updatedAtMs) {
      return displayImage;
    }

    const separator = displayImage.includes('?') ? '&' : '?';
    return `${displayImage}${separator}v=${updatedAtMs}`;
  }, [displayImage, template.updatedAt]);

  const getDifficultyColor = () => {
    switch (template.difficulty) {
      case 'easy':
        return theme.colors.success;
      case 'moderate':
        return theme.colors.warning;
      case 'adventurous':
        return theme.colors.error;
      default:
        return theme.colors.textSecondary;
    }
  };

  const getDifficultyIcon = () => {
    switch (template.difficulty) {
      case 'easy':
        return 'leaf-outline';
      case 'moderate':
        return 'trail-sign-outline';
      case 'adventurous':
        return 'flame-outline';
      default:
        return 'help-outline';
    }
  };

  const formatBudget = () => {
    const raw = template.estimatedBudget;
    const locale = 'en-US';
    const currency =
      raw?.currency && String(raw.currency).length === 3 ? String(raw.currency) : 'USD';

    const min = typeof raw?.min === 'number' && !Number.isNaN(raw.min) ? raw.min : null;
    const max = typeof raw?.max === 'number' && !Number.isNaN(raw.max) ? raw.max : null;

    if (min === null || max === null) {
      return 'Not set';
    }

    // Prefer Intl for consistent currency formatting.
    const formatCurrency = (value: number, compact: boolean) => {
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
          notation: compact ? 'compact' : 'standard',
          maximumFractionDigits: compact ? 1 : 0,
        }).format(value);
      } catch {
        const symbol = currency === 'USD' ? '$' : currency;
        return compact ? `${symbol}${Math.round(value).toLocaleString(locale)}` : `${symbol}${value.toLocaleString(locale)}`;
      }
    };

    const compact = isCompact;
    if (min === max) {
      return formatCurrency(min, compact);
    }

    // Use en dash for ranges, keep currency on both ends for clarity.
    return `${formatCurrency(min, compact)}–${formatCurrency(max, compact)}`;
  };

  const chipBg =
    theme.colorScheme === 'dark'
      ? 'rgba(30,30,30,0.88)'
      : 'rgba(255,255,255,0.92)';

  const hasRightAction = !!showSaveAction || !!showShareAction;
  const hasRating =
    typeof template.rating === 'number' &&
    Number.isFinite(template.rating) &&
    (template.reviewCount ?? 0) > 0;

  return (
    <Pressable
      onPress={() => onPress(template)}
      android_ripple={{ color: theme.colors.overlay }}
      style={({ pressed }) => [
        {
          opacity: pressed ? 0.92 : 1,
          transform: [{ scale: pressed && Platform.OS === 'ios' ? 0.995 : 1 }],
        },
      ]}
    >
      <Card
        variant="elevated"
        elevation={1}
        padding={0}
        style={{
          overflow: 'hidden',
          borderWidth: 1,
          borderColor: theme.colors.borderMuted,
        }}
      >
        {/* Cover Image */}
        <View
          style={{
            width: '100%',
            height: isCompact ? 120 : 150,
            backgroundColor: theme.colors.borderMuted,
            position: 'relative',
          }}
        >
          <AppImage
            source={{ uri: versionedDisplayImage }}
            style={{ width: '100%', height: '100%' }}
            resizeMode="cover"
            cachePolicy="memory-disk"
            accessibilityLabel={`Cover image for ${template.title}`}
          />

          {/* Top overlay row (prevents badge overlap on small cards) */}
          <View
            style={{
              position: 'absolute',
              top: theme.spacing(1),
              left: theme.spacing(1),
              right: theme.spacing(1),
              flexDirection: 'row',
              alignItems: 'flex-start',
              justifyContent: hasRightAction ? 'space-between' : 'flex-start',
              zIndex: 2,
            }}
          >
            {/* Difficulty chip (left) */}
            <View
              style={{
                maxWidth: '68%',
                backgroundColor: chipBg,
                borderWidth: 1,
                borderColor: theme.colors.borderMuted,
                paddingHorizontal: theme.spacing(1),
                paddingVertical: theme.spacing(0.5),
                borderRadius: theme.radii.pill,
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(0.5),
              }}
            >
              <Ionicons name={getDifficultyIcon()} size={12} color={getDifficultyColor()} />
              <Caption
                numberOfLines={1}
                style={{ color: getDifficultyColor(), fontWeight: '700', textTransform: 'capitalize' }}
              >
                {template.difficulty}
              </Caption>
            </View>

            {/* Right actions */}
            {hasRightAction && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                {showShareAction && (
                  <Pressable
                    onPress={(event) => {
                      event.stopPropagation?.();
                      void shareTemplate(template, {
                        source: 'template_card',
                        onCopied: () => showSuccess('Template link copied'),
                      });
                    }}
                    hitSlop={8}
                    style={{
                      backgroundColor: chipBg,
                      borderWidth: 1,
                      borderColor: theme.colors.borderMuted,
                      borderRadius: theme.radii.pill,
                      padding: theme.spacing(0.5),
                    }}
                    accessibilityRole="button"
                    accessibilityLabel={`Share ${template.title}`}
                  >
                    <Ionicons name="share-outline" size={18} color={theme.colors.text} />
                  </Pressable>
                )}
                {showSaveAction && (
                <View
                  style={{
                    backgroundColor: chipBg,
                    borderWidth: 1,
                    borderColor: theme.colors.borderMuted,
                    borderRadius: theme.radii.pill,
                    padding: theme.spacing(0.5),
                  }}
                >
                  <SaveTemplateButton userId={userId || ''} template={template} size={20} />
                </View>
                )}
              </View>
            )}
          </View>
        </View>

        {/* Content */}
        <View style={{ padding: theme.spacing(isCompact ? 1.25 : 1.5), gap: theme.spacing(0.9) }}>
          {/* Title */}
          <ThemedText variant="title" numberOfLines={2}>
            {template.title}
          </ThemedText>

          {/* Destination */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
            <Ionicons name="location-outline" size={14} color={theme.colors.textSecondary} />
            <Caption color={theme.colors.textSecondary} numberOfLines={1}>
              {template.destination}
            </Caption>
          </View>

          {/* Description */}
          {!isCompact && (
            <Body color={theme.colors.textSecondary} style={{ fontSize: 13 }} numberOfLines={2}>
              {template.shortDescription}
            </Body>
          )}

          {/* Stats Row */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: theme.spacing(0.5),
              paddingTop: theme.spacing(1),
              borderTopWidth: 1,
              borderTopColor: theme.colors.borderMuted,
              gap: theme.spacing(1),
            }}
          >
            {/* Duration */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <Ionicons name="calendar-outline" size={14} color={theme.colors.textSecondary} />
              <Caption color={theme.colors.textSecondary} numberOfLines={1}>
                {template.duration} {template.duration === 1 ? 'day' : 'days'}
              </Caption>
            </View>

            {/* Budget */}
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                flexShrink: 1,
                justifyContent: 'flex-end',
                minWidth: 0,
              }}
            >
              <Ionicons name="wallet-outline" size={14} color={theme.colors.textSecondary} />
              <Caption color={theme.colors.textSecondary} numberOfLines={1} style={{ flexShrink: 1, minWidth: 0, textAlign: 'right' }}>
                {formatBudget()}
              </Caption>
            </View>

            {/* Popularity */}
            {template.usageCount > 0 && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                <Ionicons name="people-outline" size={14} color={theme.colors.textSecondary} />
                <Caption color={theme.colors.textSecondary} numberOfLines={1}>
                  {template.usageCount}
                </Caption>
              </View>
            )}

            {/* Rating */}
            {hasRating && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                <Ionicons name="star" size={14} color={theme.colors.warning} />
                <Caption color={theme.colors.textSecondary} numberOfLines={1}>
                  {template.rating!.toFixed(1)} ({template.reviewCount})
                </Caption>
              </View>
            )}
          </View>

          {/* Budget note */}
          <Caption
            color={theme.colors.textSecondary}
            style={{ fontSize: 11 }}
          >
            {isCompact ? 'Est. per person' : 'Estimated per person'}
          </Caption>

          {/* Travel styles + topic tags (e.g. World Cup collection) */}
          {!isCompact && ((template.travelStyle ?? []).length > 0 || visibleTopicTags.length > 0) && (
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: theme.spacing(0.5),
                marginTop: theme.spacing(0.5),
              }}
            >
              {(template.travelStyle ?? []).slice(0, 3).map((style) => (
                <View
                  key={style}
                  style={{
                    backgroundColor: theme.colors.background,
                    paddingHorizontal: theme.spacing(1),
                    paddingVertical: theme.spacing(0.25),
                    borderRadius: theme.radii.sm,
                    borderWidth: 1,
                    borderColor: theme.colors.borderMuted,
                  }}
                >
                  <Caption style={{ fontSize: 11, textTransform: 'capitalize' }}>{style}</Caption>
                </View>
              ))}
              {visibleTopicTags.map((tag) => (
                <View
                  key={`tag-${tag}`}
                  style={{
                    backgroundColor: `${theme.colors.primary}12`,
                    paddingHorizontal: theme.spacing(1),
                    paddingVertical: theme.spacing(0.25),
                    borderRadius: theme.radii.sm,
                    borderWidth: 1,
                    borderColor: `${theme.colors.primary}40`,
                  }}
                >
                  <Caption style={{ fontSize: 11, color: theme.colors.primary, fontWeight: '600' }}>
                    {formatTemplateTagLabel(tag)}
                  </Caption>
                </View>
              ))}
              {hiddenTopicCount > 0 && (
                <View
                  style={{
                    backgroundColor: theme.colors.background,
                    paddingHorizontal: theme.spacing(1),
                    paddingVertical: theme.spacing(0.25),
                    borderRadius: theme.radii.sm,
                    borderWidth: 1,
                    borderColor: theme.colors.borderMuted,
                    justifyContent: 'center',
                  }}
                >
                  <Caption style={{ fontSize: 11, color: theme.colors.textSecondary }}>
                    +{hiddenTopicCount}
                  </Caption>
                </View>
              )}
            </View>
          )}
        </View>
      </Card>
    </Pressable>
  );
}

/**
 * TemplateCardSkeleton - Loading state for template cards
 */
export function TemplateCardSkeleton() {
  const theme = useTheme();

  return (
    <Card
      variant="elevated"
      elevation={1}
      padding={0}
      style={{
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: theme.colors.borderMuted,
      }}
    >
      {/* Image skeleton */}
      <View
        style={{
          width: '100%',
          height: 160,
          backgroundColor: theme.colors.borderMuted,
        }}
      />

      {/* Content skeleton */}
      <View style={{ padding: theme.spacing(1.5), gap: theme.spacing(1) }}>
        {/* Title skeleton */}
        <View
          style={{
            height: 18,
            width: '80%',
            backgroundColor: theme.colors.borderMuted,
            borderRadius: theme.radii.sm,
          }}
        />

        {/* Destination skeleton */}
        <View
          style={{
            height: 14,
            width: '50%',
            backgroundColor: theme.colors.borderMuted,
            borderRadius: theme.radii.sm,
          }}
        />

        {/* Description skeleton */}
        <View
          style={{
            height: 12,
            width: '100%',
            backgroundColor: theme.colors.borderMuted,
            borderRadius: theme.radii.sm,
          }}
        />
        <View
          style={{
            height: 12,
            width: '90%',
            backgroundColor: theme.colors.borderMuted,
            borderRadius: theme.radii.sm,
          }}
        />

        {/* Stats skeleton */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: theme.spacing(1),
            paddingTop: theme.spacing(1),
            borderTopWidth: 1,
            borderTopColor: theme.colors.borderMuted,
          }}
        >
          <View
            style={{
              height: 14,
              width: 60,
              backgroundColor: theme.colors.borderMuted,
              borderRadius: theme.radii.sm,
            }}
          />
          <View
            style={{
              height: 14,
              width: 80,
              backgroundColor: theme.colors.borderMuted,
              borderRadius: theme.radii.sm,
            }}
          />
        </View>
      </View>
    </Card>
  );
}
